import { ComponentConfig, registerOverlayPortal } from '@measured/puck';
import { TabsContent } from '@radix-ui/react-tabs';
import { clsx } from 'clsx';
import { useEffect, useRef } from 'react';

import { getIconComponent } from '../../../../../components/atoms/icon/icon-utils';
import { RenderSlot } from '../../../../../utils/render-slot';
import { ExportPDFButton } from '../../../components/export-pdf-button';
import { CurrentReportCompletionLabel, ReportSwitcher } from '../../../components/report-switcher/report-switcher';
import { useReportContext } from '../../../renderer/report-context';
import { Tabs, TabsList, TabsTrigger } from '../../atoms/tabs/tabs';

import { createReportTabsConfig, TabType } from './create-report-tabs-config';

type ReportTabsProps = {
  readonly tabs: TabType[];
  readonly showExportButton: boolean;
  readonly isPdf: boolean;
  readonly showReportSwitcher: boolean;
  readonly reportStatus?: string;
  readonly reportTimestamp?: string;
};

const useInitialNavigateToFirstTab = ({ tabs, isPdf }: Pick<ReportTabsProps, 'tabs' | 'isPdf'>) => {
  const { path: selectedTab, navigateToPath } = useReportContext();
  const initialised = useRef(false);

  useEffect(() => {
    const [firstTab] = tabs;

    if (!initialised.current && !selectedTab && tabs.length && firstTab && !isPdf) {
      initialised.current = true;
      navigateToPath(firstTab.slug);
    }
  }, [navigateToPath, selectedTab, tabs, isPdf]);
};

const TabTrigger = ({ slug, icon, name }: Pick<TabType, 'icon' | 'slug' | 'name'>) => {
  const ref = useRef(null);
  const IconComponent = icon ? getIconComponent(icon) : null;
  useEffect(() => registerOverlayPortal(ref.current), []);

  return (
    <TabsTrigger
      className="data-[state=active]:text-background data-[state=active]:bg-primary h-full border-none"
      ref={ref}
      value={slug}
    >
      {IconComponent && <IconComponent size={24} />}
      {name}
    </TabsTrigger>
  );
};

const ReportTabsHeader = ({
  tabs,
  showExportButton,
  showReportSwitcher,
  exportPdf,
  navigateToPath,
}: {
  readonly exportPdf: () => void;
  readonly navigateToPath: (path: string) => void;
  readonly tabs: TabType[];
  readonly showExportButton: boolean;
  readonly showReportSwitcher: boolean;
}) => {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <TabsList className="border-neutral-700 border-1 rounded-[9px] p-0">
          {tabs.map(({ slug, name, icon }) => (
            <TabTrigger icon={icon} key={slug} name={name} slug={slug} />
          ))}
        </TabsList>
        {showExportButton && <ExportPDFButton onClick={exportPdf} />}
      </div>
      {showReportSwitcher && <ReportSwitcher navigateOnChange={navigateToPath} />}
    </div>
  );
};

type WebTabsProps = Omit<ReportTabsProps, 'isPdf'>;
const WebTabs = ({ tabs = [], showExportButton, showReportSwitcher }: WebTabsProps) => {
  const { path: selectedTab, navigateToPath, exportPdf } = useReportContext();
  return (
    <Tabs className="w-full" defaultValue={selectedTab} onValueChange={navigateToPath} value={selectedTab}>
      <ReportTabsHeader
        exportPdf={exportPdf}
        navigateToPath={navigateToPath}
        showExportButton={Boolean(showExportButton)}
        showReportSwitcher={Boolean(showReportSwitcher)}
        tabs={tabs}
      />
      {tabs.map(({ slug, content }: TabType) => (
        <TabsContent
          key={slug}
          style={{
            display: slug === selectedTab ? 'block' : 'none',
          }}
          value={slug}
        >
          <RenderSlot slot={content} />
        </TabsContent>
      ))}
    </Tabs>
  );
};

const pdfTabContainerClasses = (idx: number) => clsx({ 'print:break-inside-avoid': idx > 0 });

type PdfTabsProps = Omit<ReportTabsProps, 'isPdf' | 'showExportButton' | 'showReportSwitcher'>;
const PdfTabs = ({ tabs = [], reportStatus, reportTimestamp }: PdfTabsProps) => {
  return (
    <div className="flex flex-col columns gap-2">
      {(reportStatus || reportTimestamp) && (
        <div className="flex justify-end pr-4">
          <CurrentReportCompletionLabel status={reportStatus} timestamp={reportTimestamp} />
        </div>
      )}
      {tabs.map(({ content, slug }: TabType, idx) => (
        <div className={pdfTabContainerClasses(idx)} key={slug}>
          <RenderSlot slot={content} />
        </div>
      ))}
    </div>
  );
};

const ReportTabs = ({
  isPdf,
  tabs = [],
  reportStatus,
  reportTimestamp,
  showExportButton,
  showReportSwitcher,
}: ReportTabsProps) => {
  useInitialNavigateToFirstTab({ tabs, isPdf });

  return isPdf ? (
    <PdfTabs reportStatus={reportStatus} reportTimestamp={reportTimestamp} tabs={tabs} />
  ) : (
    <WebTabs showExportButton={showExportButton} showReportSwitcher={showReportSwitcher} tabs={tabs} />
  );
};

const createReportTabsBlock = ({
  isPdf = false,
  reportStatus,
  reportTimestamp,
}: {
  isPdf?: boolean;
  reportStatus?: string;
  reportTimestamp?: string;
}): ComponentConfig<Pick<ReportTabsProps, 'tabs'>> => {
  return {
    fields: {
      tabs: {
        ...createReportTabsConfig().fieldConfig,
      },
    },
    label: 'Report Tabs',
    defaultProps: {
      tabs: createReportTabsConfig().defaultValue,
    },
    render: (props: Pick<ReportTabsProps, 'tabs'>) => (
      <ReportTabs
        {...props}
        isPdf={isPdf}
        reportStatus={reportStatus}
        reportTimestamp={reportTimestamp}
        showExportButton
        showReportSwitcher
      />
    ),
  };
};

export { createReportTabsBlock, ReportTabs };

export type { ReportTabsProps };
